import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import {
  fetchSeriesDetail,
  fetchEpisodeDetails,
  fetchSimilarContent,
  fetchGenres,
  fetchAgeRatings,
  resolveRentalForPackages,
  getImageUrl,
} from "../../redux/content";
import { useSelector } from "react-redux";
import { fetchPurchaseHistory } from "../../redux/subscription";
import "../../components/styles/movieDetailsPage.scss";

/**
 * SeriesDetailsPage — Series (TV show) detail screen.
 *
 * Reuses the MovieDetailsPage `md-page` design system + the season/episode
 * layout already defined in movieDetailsPage.scss. Data flow mirrors
 * PodcastDetailPage (which is also a series), since the `/series/{id}` endpoint
 * returns a different shape than `/movies/{id}` (seasons/episodes, `images` map).
 *
 * Data flow:
 *   1. fetchSeriesDetail(id)        — title, cast, director, genres, seasons, episodes
 *   2. fetchEpisodeDetails(epIds)   — per-episode descriptions/durations/languages
 *   3. fetchSimilarContent(id, "series") — "More like this"
 *   4. resolveProductIdForPackages  — Subscribe CTA deep-link
 *
 * Routed via DetailsPage dispatcher when the content type === "series".
 */
const SeriesDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const similarMovies = useSelector((state) => state.content.similarMovies);
  const activeProductIds = useSelector((state) => state.subscription.activeProductIds);

  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSeason, setActiveSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodeDetails, setEpisodeDetails] = useState({});
  const [isSeasonOpen, setIsSeasonOpen] = useState(false);
  const [genreNames, setGenreNames] = useState([]);
  const [ageRatingLabel, setAgeRatingLabel] = useState("");
  const [pickPlanProductId, setPickPlanProductId] = useState(null);
  const [rentalPrice, setRentalPrice] = useState(null);
  const [productResolved, setProductResolved] = useState(false);
  const [purchaseHistoryReady, setPurchaseHistoryReady] = useState(false);
  const seasonRef = useRef(null);

  // Active navbar tab — infer from where the user came (mirror MovieDetailsPage)
  const navActiveTab = location.state?.fromTab || "";

  // ── Fetch series + refresh purchase history on id change
  useEffect(() => {
    if (!id) return;
    console.log("[SeriesDetailsPage] Fetching series:", id);
    setLoading(true);
    setSeries(null);
    setActiveSeason(null);
    setEpisodes([]);
    setEpisodeDetails({});
    setProductResolved(false);
    setPurchaseHistoryReady(false);

    const load = async () => {
      const data = await fetchSeriesDetail(id);
      if (data) {
        console.log("[SeriesDetailsPage] Series loaded:", data.title, "| seasons:", data.seasons?.length);
        setSeries(data);
        if (data.seasons?.length > 0) setActiveSeason(0);
      } else {
        console.warn("[SeriesDetailsPage] Series not found for id:", id);
      }
      setLoading(false);
    };
    load();
    fetchSimilarContent(id, "series");
    fetchPurchaseHistory("All").finally(() => setPurchaseHistoryReady(true));
  }, [id]);

  // ── Resolve genres + age rating once the series loads
  useEffect(() => {
    if (!series) return;
    const resolveMeta = async () => {
      const [genreMap, ageMap] = await Promise.all([fetchGenres(), fetchAgeRatings()]);
      const gIds = series.genres || [];
      const names = gIds.map((g) => genreMap[g.id || g] || "").filter(Boolean);
      setGenreNames(names);
      if (series.age_rating_id && ageMap[series.age_rating_id]) {
        setAgeRatingLabel(ageMap[series.age_rating_id]);
      }
      console.log("[SeriesDetailsPage] Genres resolved:", names.join(", "), "| age:", ageMap[series.age_rating_id]);
    };
    resolveMeta();
  }, [series]);

  // ── Resolve the TV Store product owning this series' packages (Subscribe CTA)
  useEffect(() => {
    if (!series) return;
    const packages = series.packages || [];
    if (packages.length === 0) {
      console.log("[SeriesDetailsPage] No packages on series, skipping product resolve");
      setProductResolved(true);
      return;
    }
    const resolve = async () => {
      const { productId, price } = await resolveRentalForPackages(packages);
      console.log("[SeriesDetailsPage] Resolved productId:", productId, "price:", price);
      if (productId) setPickPlanProductId(productId);
      if (price != null) setRentalPrice(price);
      setProductResolved(true);
    };
    resolve();
  }, [series]);

  // ── When active season changes, set episodes + fetch their full details
  useEffect(() => {
    if (!series?.seasons || activeSeason === null) return;
    const season = series.seasons[activeSeason];
    if (!season?.episodes?.length) {
      console.log("[SeriesDetailsPage] No episodes in season", activeSeason);
      setEpisodes([]);
      setEpisodeDetails({});
      return;
    }
    const seasonEpisodes = season.episodes;
    setEpisodes(seasonEpisodes);
    console.log("[SeriesDetailsPage] Season", activeSeason + 1, "has", seasonEpisodes.length, "episodes");

    const loadEpisodeDetails = async () => {
      const epIds = seasonEpisodes.map((ep) => ep.id);
      const details = await fetchEpisodeDetails(epIds);
      setEpisodeDetails(details);
      console.log("[SeriesDetailsPage] Episode details loaded for", Object.keys(details).length, "episodes");
    };
    loadEpisodeDetails();
  }, [series, activeSeason]);

  // ── Close season dropdown on click-outside / Escape
  useEffect(() => {
    if (!isSeasonOpen) return;
    const handleClickOutside = (e) => {
      if (seasonRef.current && !seasonRef.current.contains(e.target)) setIsSeasonOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setIsSeasonOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isSeasonOpen]);

  // ── Derived display values
  // Series detail exposes images as `images: { POSTER, PREVIEW, TITLE_ART, ... }`.
  const heroPreviewId = series?.images?.PREVIEW;
  const heroImage = heroPreviewId
    ? getImageUrl({ image_id: heroPreviewId })
    : (series ? getImageUrl(series, "BANNER") || getImageUrl(series, "POSTER") : null);
  const titleArtId = series?.images?.TITLE_ART;
  const titleArtUrl = titleArtId ? getImageUrl({ image_id: titleArtId }) : null;
  const title = series?.title || "";
  const seasonsCount = series?.seasons?.length || 0;
  const castList = series?.cast
    ? series.cast.split(",").map((name) => ({ name: name.trim() })).filter((c) => c.name)
    : [];

  // Aggregate languages from loaded episode details
  const allAudioLangs = new Set();
  const allSubtitleLangs = new Set();
  Object.values(episodeDetails).forEach((ep) => {
    if (ep.audio_languages) ep.audio_languages.forEach((l) => allAudioLangs.add(l));
    if (ep.subtitle_languages) ep.subtitle_languages.forEach((l) => allSubtitleLangs.add(l));
  });

  // Entitlement: CRM purchase history (activeProductIds) is the source of truth,
  // mirroring MovieDetailsPage. A matching resolved product id ⇒ user has access.
  const isPurchased =
    pickPlanProductId != null &&
    Array.isArray(activeProductIds) &&
    activeProductIds.some((pid) => String(pid) === String(pickPlanProductId));
  const buttonReady = productResolved && purchaseHistoryReady;

  const playEpisode = (ep) => {
    if (!ep) return;
    console.log("[SeriesDetailsPage] Playing episode:", ep.id, ep.title);
    navigate(`/player/${ep.id}?type=series`, {
      state: { movieId: ep.id, title: ep.title || `Episode ${ep.number}`, seriesTitle: title },
    });
  };

  const handlePlayOrSubscribe = () => {
    if (isPurchased || !pickPlanProductId) {
      // Owned (or free/unresolved) → play the first available episode
      playEpisode(episodes[0]);
      return;
    }
    console.log("[SeriesDetailsPage] Subscribe → PickPlanPage", pickPlanProductId);
    navigate(`/discover/product/${pickPlanProductId}/plan`, {
      state: { returnPath: `/details/${id}` },
    });
  };

  if (loading) {
    return (
      <div className="md-page">
        <Navbar activeTab={navActiveTab} />
        <div className="md-page__loader">
          <div className="md-page__loader-ring" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!series) {
    return (
      <div className="md-page">
        <Navbar activeTab={navActiveTab} />
        <div className="md-page__loader">Series not found</div>
        <Footer />
      </div>
    );
  }

  console.log("[SeriesDetailsPage] rendering:", title, "purchased:", isPurchased, "similar:", similarMovies.length);

  return (
    <div className="md-page">
      <Navbar activeTab={navActiveTab} />

      {/* Hero Section */}
      <section className="md-page__hero">
        <div className="md-page__hero-bg">
          <img
            src={heroImage || "/assets/movie-details/ted-lasso-hero.webp"}
            alt={title}
            className="md-page__hero-bg-img"
          />
        </div>
        <div className="md-page__hero-gradient" />

        <div className="md-page__hero-content">
          <div className="md-page__hero-left">
            {titleArtUrl ? (
              <img src={titleArtUrl} alt={title} className="md-page__title-art" />
            ) : (
              <h1 className="md-page__show-title">{title || "Loading..."}</h1>
            )}

            <div className="md-page__actions">
              <button
                className="md-page__rent-btn"
                onClick={handlePlayOrSubscribe}
                disabled={!buttonReady}
              >
                {buttonReady ? (
                  <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                    <path d="M1 1L13 8L1 15V1Z" fill="#0a0a0a" />
                  </svg>
                ) : (
                  <span className="md-page__rent-btn-spinner" aria-hidden="true" />
                )}
                {buttonReady
                  ? (isPurchased
                      ? "Play"
                      : rentalPrice != null
                      ? `Rent (₦${parseFloat(rentalPrice).toLocaleString()})`
                      : pickPlanProductId
                      ? "Subscribe"
                      : "Play")
                  : null}
              </button>
            </div>
          </div>

          <div className="md-page__hero-right">
            <div className="md-page__credits">
              {series.director && (
                <div className="md-page__credit">
                  <span className="md-page__credit-label">Director</span>
                  <span className="md-page__credit-value">{series.director}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="md-page__body">
        {/* Seasons + Episodes */}
        <section className="md-page__seasons">
          <div
            className="md-page__season-selector"
            ref={seasonRef}
            onClick={() => { if (seasonsCount > 1) setIsSeasonOpen((prev) => !prev); }}
            aria-expanded={isSeasonOpen}
            role={seasonsCount > 1 ? "button" : undefined}
            tabIndex={seasonsCount > 1 ? 0 : undefined}
            onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && seasonsCount > 1) { e.preventDefault(); setIsSeasonOpen((prev) => !prev); } }}
          >
            <span>{series.seasons?.[activeSeason]?.title || `Season ${activeSeason + 1}`}</span>
            {seasonsCount > 1 && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ transform: isSeasonOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            )}
            {isSeasonOpen && seasonsCount > 1 && (
              <ul className="md-page__season-dropdown" role="listbox" onClick={(e) => e.stopPropagation()}>
                {series.seasons.map((s, i) => (
                  <li
                    key={s.id || i}
                    className={`md-page__season-option${i === activeSeason ? " md-page__season-option--active" : ""}`}
                    role="option"
                    aria-selected={i === activeSeason}
                    onClick={() => {
                      console.log("[SeriesDetailsPage] Season selected:", i);
                      setActiveSeason(i);
                      setIsSeasonOpen(false);
                    }}
                  >
                    {s.title || `Season ${s.number}`}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="md-page__episodes">
            {episodes.map((ep, i) => {
              const detail = episodeDetails[ep.id];
              const epDescription = detail?.description || "";
              // Prefer the episode's preview poster (landscape) from the episode
              // details API; fall back to the series-list episode image.
              const epPreviewId = detail?.images?.PREVIEW;
              const epThumb = epPreviewId
                ? getImageUrl({ image_id: epPreviewId })
                : getImageUrl({ image_id: ep.image_id, images: detail?.images });
              console.log("[SeriesDetailsPage] episode thumb:", ep.number, "previewPoster:", epPreviewId, "thumb:", epThumb);
              const epDuration = ep.duration ? `${ep.duration} min` : "";
              return (
                <div
                  key={ep.id || i}
                  className="md-page__episode"
                  onClick={() => playEpisode(ep)}
                  role="button"
                  tabIndex={0}
                >
                  <div className="md-page__episode-thumb">
                    {epThumb ? (
                      <img src={epThumb} alt={ep.title || ""} className="md-page__episode-thumb-img" />
                    ) : null}
                    <img src="/assets/movie-details/add-circle-icon.svg" alt="" className="md-page__episode-play" style={{ visibility: "hidden" }} />
                  </div>
                  <div className="md-page__episode-info">
                    <span className="md-page__episode-num">Episode {ep.number}</span>
                    <h3 className="md-page__episode-title">{ep.title || `Episode ${ep.number}`}</h3>
                    <div className="md-page__episode-meta">
                      {detail?.resolution && <span>{detail.resolution}</span>}
                      {epDuration && <span>{epDuration}</span>}
                    </div>
                    {epDescription && <p className="md-page__episode-desc">{epDescription}</p>}
                  </div>
                </div>
              );
            })}
            {episodes.length === 0 && (
              <p className="md-page__info-text">No episodes available</p>
            )}
          </div>
        </section>

        {/* Cast & Crew */}
        {castList.length > 0 && (
          <section className="md-page__cast-section">
            <h2 className="md-page__section-title">Cast &amp; Crew</h2>
            <div className="md-page__cast-list">
              {castList.map((person, i) => (
                <div key={i} className="md-page__cast-item">
                  <div className="md-page__cast-avatar md-page__cast-avatar--placeholder">
                    <span>{person.name[0]}</span>
                  </div>
                  <span className="md-page__cast-name">{person.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/*
          "More like this" section disabled.
          The related endpoint (GET /vod/series/{id}/related) returns lean items
          with only a portrait `image_id` — no landscape preview — so the
          landscape cards rendered cropped portraits. Re-enable once the backend
          provides a landscape image on related items, or add per-item detail
          fetching to resolve each PREVIEW image.

          {similarMovies.length > 0 && (
            <section className="md-page__more-section">
              <h2 className="md-page__section-title">More like this</h2>
              <div className="md-page__more-list">
                {similarMovies.map((item, i) => {
                  const img = getImageUrl(item, "POSTER") || getImageUrl(item);
                  return (
                    <div
                      key={item.id || i}
                      className="md-page__more-card"
                      onClick={() => navigate(`/details/${item.id}`, { state: { contentType: item.type, fromTab: navActiveTab } })}
                      style={{ cursor: "pointer" }}
                    >
                      {img ? (
                        <img src={img} alt={item.title || ""} className="md-page__more-img" />
                      ) : (
                        <div className="md-page__more-placeholder">{item.title || "?"}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        */}

        {/* Synopsis / Information / Languages */}
        <section className="md-page__info-grid">
          <div className="md-page__info-col">
            <h3 className="md-page__info-heading">Synopsis</h3>
            <p className="md-page__info-text">{series.description || "No synopsis available."}</p>
          </div>
          <div className="md-page__info-right">
            <div className="md-page__info-col">
              <h3 className="md-page__info-heading">Information</h3>
              {seasonsCount > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Seasons</span>
                  <span className="md-page__info-value">{seasonsCount} Season{seasonsCount !== 1 ? "s" : ""}</span>
                </div>
              )}
              {series.year && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Release Date</span>
                  <span className="md-page__info-value">{series.year}</span>
                </div>
              )}
              {genreNames.length > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Genre</span>
                  <span className="md-page__info-value">{genreNames.join(", ")}</span>
                </div>
              )}
              {ageRatingLabel && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Rated</span>
                  <span className="md-page__info-value">{ageRatingLabel}</span>
                </div>
              )}
              {series.country?.length > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Country</span>
                  <span className="md-page__info-value">{series.country.join(", ")}</span>
                </div>
              )}
            </div>
            <div className="md-page__info-col">
              <h3 className="md-page__info-heading">Languages</h3>
              {allAudioLangs.size > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Original Audio</span>
                  <span className="md-page__info-value">{[...allAudioLangs].join(", ")}</span>
                </div>
              )}
              {allSubtitleLangs.size > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Subtitles</span>
                  <span className="md-page__info-value">{[...allSubtitleLangs].join(", ")}</span>
                </div>
              )}
              {allAudioLangs.size === 0 && allSubtitleLangs.size === 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-value">No language info available</span>
                </div>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default SeriesDetailsPage;
